import { Portal } from '@gorhom/portal'
import { isWeb } from '@lib/constants'
import { ToastWithId, useToastsStore } from '@stores/toasts'
import { Colors } from '@vetahealth/fishing-gear/colors'
import React, { useEffect } from 'react'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'
import Animated, { FadeInUp, FadeOutUp, useAnimatedStyle, useSharedValue, withSpring } from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { scheduleOnRN } from 'react-native-worklets'
import styled, { useTheme } from 'styled-components/native'
import { Markdown } from '../../Markdown'
import { IconButton } from '../Button'
import { FullWindowOverlay } from '../FullWindowOverlay'
import { Icon } from '../Icon'
import { Pressable } from '../Pressable'
import { Text } from '../Text'

const Container = styled.View<{ top: number }>`
  position: absolute;
  top: ${({ top = 0 }) => top}px;
  left: 20px;
  right: 20px;
  z-index: 3;
`
const Wrapper = styled(Animated.View)`
  flex-direction: row;
  align-self: center;
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius.medium}px;
  padding: 20px;
  margin: 5px 20px;
  width: 100%;
  pointer-events: auto;
  box-shadow: 0px 0px 4px ${Colors.black}11;
`
const ToastColor = styled.View`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  border-radius: ${({ theme }) => theme.borderRadius.medium}px;
  background-color:  ${({ theme }) => theme.colors.toastBackground};
`
const ContentWrapper = styled.View<{ hasAction: boolean }>`
  ${({ hasAction }) => (hasAction || isWeb) && 'flex: 1;'}
`
const ContentHeaderWrapper = styled.View<{ hasMessage: boolean }>`
  flex-direction: row;
  align-items: center;
  margin-bottom: ${({ hasMessage }) => (hasMessage ? 10 : 0)}px;
`
const IconWrapper = styled.View<{ color?: Colors }>`
  border-radius: ${({ theme }) => theme.borderRadius.large}px;
  background-color: ${({ color, theme }) => color || theme.colors.primary};
  width: 26px;
  height: 26px;
  justify-content: center;
  align-items: center;
  margin-right: 10px;
`
const ActionWrapper = styled.View`
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 5px;
`
const ToastButton = styled(Pressable)`
  padding: 5px 10px 0;
  margin-left: 20px;
`
const CancelWrapper = styled.View<{ isCentered?: boolean }>`
  right: 0px;
  top: 0px;
  justify-content: ${({ isCentered }) => (isCentered ? 'center' : 'flex-start')};
  margin: -10px -10px -10px 10px;
`

const DEFAULT_DURATION = 12000 // 12s

function Toast({
  title,
  message,
  id,
  duration,
  icon,
  isCancable = true,
  onPress,
  infinite,
  primaryAction,
  secondaryAction,
  backgroundAction,
}: ToastWithId): React.ReactNode {
  const theme = useTheme()
  const [removeToast] = useToastsStore((state) => [state.removeToast])
  const translateY = useSharedValue(0)

  useEffect(() => {
    backgroundAction?.()

    const timer =
      !infinite &&
      setTimeout(() => {
        removeToast(id)
      }, duration || DEFAULT_DURATION)

    return () => {
      if (!infinite && timer) clearTimeout(timer)
    }
  }, [])

  const pan = Gesture.Pan()
    .onUpdate((event) => {
      if (event.translationY < 0) {
        translateY.value = event.translationY
      }
    })
    .onEnd(() => {
      if (isCancable && translateY.value <= -30) {
        scheduleOnRN(removeToast, id)
      } else {
        translateY.value = withSpring(0)
      }
    })

  const tap = Gesture.Tap()
    .onEnd(() => {
      if (onPress) {
        scheduleOnRN(removeToast, id)
        scheduleOnRN(onPress)
      }
    })
    .numberOfTaps(1)

  const style = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }))

  function handleCancelPress() {
    removeToast(id)
  }

  function handleActionPress(action: () => void): () => void {
    return () => {
      handleCancelPress()
      action()
    }
  }

  const hasAction = Boolean(primaryAction || secondaryAction)

  return (
    <GestureDetector gesture={Gesture.Race(tap, pan)}>
      <Animated.View entering={FadeInUp.springify()} exiting={FadeOutUp.springify()}>
        <Wrapper style={[style]}>
          <ToastColor />
          <ContentWrapper hasAction={hasAction}>
            {(title || icon) && (
              <ContentHeaderWrapper hasMessage={!!message}>
                {!!icon && (
                  <IconWrapper color={theme.colors.toastText}>
                    <Icon icon={icon} color={theme.colors.toastBackground} size={14} />
                  </IconWrapper>
                )}
                {!!title && (
                  <Text size="settings" weight="bold" color={theme.colors.toastText}>
                    {title}
                  </Text>
                )}
              </ContentHeaderWrapper>
            )}
            {!!message && (
              <Markdown
                text={message}
                textStyle={{ weight: 'semiBold', fontSize: 'toast', color: theme.colors.toastText }}
                withoutParagraphSpacing
              />
            )}
            {hasAction && (
              <ActionWrapper>
                {secondaryAction && (
                  <ToastButton
                    onPress={handleActionPress(secondaryAction.onPress)}
                    render={() => (
                      <Text color={theme.colors.toastText} weight="bold" size="button">
                        {secondaryAction.label}
                      </Text>
                    )}
                  />
                )}
                {primaryAction && (
                  <ToastButton
                    onPress={handleActionPress(primaryAction.onPress)}
                    render={() => (
                      <Text color={theme.colors.toastText} weight="bold" size="button">
                        {primaryAction.label}
                      </Text>
                    )}
                  />
                )}
              </ActionWrapper>
            )}
          </ContentWrapper>
          {isWeb && isCancable && (
            <CancelWrapper isCentered={!title || !message}>
              <IconButton
                icon={['fas', 'xmark']}
                variant="plain"
                color={theme.colors.closeToast}
                onPress={handleCancelPress}
                size="small"
              />
            </CancelWrapper>
          )}
        </Wrapper>
      </Animated.View>
    </GestureDetector>
  )
}

export function Toasts(): React.ReactNode {
  const { top } = useSafeAreaInsets()
  const [toasts] = useToastsStore((state) => [state.toasts])

  return (
    <Portal hostName="toast">
      <FullWindowOverlay>
        <Container top={top}>
          {toasts.map(({ key, ...toast }) => (
            <Toast key={key || toast.id} {...toast} />
          ))}
        </Container>
      </FullWindowOverlay>
    </Portal>
  )
}
